/**
 * The organisation's spam quarantine (ADR-0048): with it on, mail certain to
 * be spam waits here instead of being refused, for an administrator to
 * release to the person's Inbox or delete. Held mail goes after 30 days.
 * A message is shown as text only — never rendered, links never followed.
 */

import { useContext, useState } from 'react';

import { api } from './api';
import { AdminWhereContext } from './mailboxes';
import { ActionButton, Badge, Loaded, useLoad, when } from './ui';

export interface QuarantineItem {
  readonly id: string;
  readonly held_at: string;
  readonly recipient: string;
  readonly sender: string;
  readonly subject: string | null;
  readonly score: number | null;
  readonly symbols: readonly string[];
  readonly status: 'held' | 'released' | 'deleted';
  readonly decided_by: string | null;
  readonly decided_at: string | null;
}

interface QuarantineList {
  readonly enabled: boolean;
  readonly keep_days: number;
  readonly items: readonly QuarantineItem[];
}

export function QuarantinePanel({ slug }: { slug: string }) {
  const { base } = useContext(AdminWhereContext);
  const [all, setAll] = useState(false);
  const [load, reload] = useLoad(
    (signal) =>
      api<QuarantineList>(`${base(slug)}/quarantine${all ? '?all=true' : ''}`, { signal }),
    `quarantine:${slug}:${all}`,
  );
  const [open, setOpen] = useState<string | null>(null);
  const act = async (id: string, what: 'release' | 'delete') => {
    await api(`${base(slug)}/quarantine/${id}/${what}`, { method: 'POST' });
    setOpen(null);
    reload();
  };
  return (
    <section className="adm-panel">
      <h3>Spam quarantine</h3>
      <Loaded load={load}>
        {(q) => (
          <>
            <div className="adm-inline">
              <span>
                {q.enabled ? <Badge tone="ok">On</Badge> : <Badge tone="neutral">Off</Badge>}
              </span>
              <ActionButton
                tone="ghost"
                run={async () => {
                  await api(`${base(slug)}/quarantine-policy`, {
                    method: 'POST',
                    body: { enabled: !q.enabled },
                  });
                  reload();
                }}
              >
                {q.enabled ? 'Turn off: refuse certain spam' : 'Turn on: hold certain spam here'}
              </ActionButton>
              <label className="adm-check">
                <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />{' '}
                Show released and deleted too
              </label>
            </div>
            <p className="adm-muted adm-small">
              Mail scored as certain spam is refused at the door unless this is on; then it waits
              here {q.keep_days} days for you to release or delete it. Viruses and senders your
              policy blocks are always refused.
            </p>
            {q.items.length === 0 ? (
              <p className="adm-muted">Nothing {all ? 'here' : 'waiting'}.</p>
            ) : (
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Held</th>
                    <th>For</th>
                    <th>From</th>
                    <th>Subject</th>
                    <th className="adm-num">Score</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {q.items.map((i) => (
                    <tr key={i.id}>
                      <td>{when(i.held_at)}</td>
                      <td className="adm-mono">{i.recipient}</td>
                      <td className="adm-mono">{i.sender || '—'}</td>
                      <td>
                        <button type="button" className="adm-copy" onClick={() => setOpen(i.id)}>
                          {i.subject || '(no subject)'}
                        </button>
                      </td>
                      <td className="adm-num" title={i.symbols.join(' ')}>
                        {i.score?.toFixed(1) ?? '—'}
                      </td>
                      <td>
                        {i.status === 'held' ? (
                          <span className="adm-inline">
                            <ActionButton
                              tone="ghost"
                              confirm="Release this message to the recipient's inbox? It was held as likely spam."
                              run={() => act(i.id, 'release')}
                            >
                              Release
                            </ActionButton>
                            <ActionButton
                              tone="ghost"
                              confirm="Delete this held message? It cannot be brought back."
                              run={() => act(i.id, 'delete')}
                            >
                              Delete
                            </ActionButton>
                          </span>
                        ) : (
                          <span className="adm-muted adm-small">
                            {i.status} by {i.decided_by}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Loaded>
      {open && <HeldMessage slug={slug} id={open} onClose={() => setOpen(null)} />}
    </section>
  );
}

function HeldMessage({ slug, id, onClose }: { slug: string; id: string; onClose: () => void }) {
  const { base } = useContext(AdminWhereContext);
  const [load] = useLoad(
    (signal) =>
      api<{ item: QuarantineItem; text: string; attachments: string[] }>(
        `${base(slug)}/quarantine/${id}`,
        { signal },
      ),
    `quarantine-item:${id}`,
  );
  return (
    <div className="adm-reveal">
      <Loaded load={load}>
        {({ item, text, attachments }) => (
          <>
            <strong>{item.subject || '(no subject)'}</strong>
            <p className="adm-muted adm-small">
              From {item.sender || 'unknown'} to {item.recipient}; why: {item.symbols.join(', ')}
            </p>
            <pre className="adm-details">{text || '(no text)'}</pre>
            {attachments.length > 0 && (
              <p className="adm-small">Attachments: {attachments.join(', ')}</p>
            )}
          </>
        )}
      </Loaded>
      <button type="button" className="adm-copy" onClick={onClose}>
        Close
      </button>
    </div>
  );
}
